import React, { useState, useEffect } from 'react';
import ModalWindow from '../modalWindow/ModalWindow';
import './IconPicker.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const IconPicker = ({ 
  isOpen, 
  onClose, 
  onSelect,
  position,
  currentIcon = null,
  onDragEnd,
  zIndex,
  onFocus
}) => {
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(24);

  // Keep track of the last search term that was used
  const [activeSearch, setActiveSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Use the active search term when refetching
      fetchIcons(activeSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentPage]); // Refetch when page changes

    const fetchIcons = async (search = '') => {
    try {
      setLoading(true);
      setError(null);

      // Ensure page number is valid
      if (currentPage < 1) {
        console.log('Invalid page number, resetting to page 1');
        setCurrentPage(1);
      }
      
      // Use search endpoint if there's a search term, otherwise use all endpoint
      const endpoint = search 
        ? `${API_URL}/api/icons/search?query=${encodeURIComponent(search)}&page=${currentPage}&count=${itemsPerPage}`
        : `${API_URL}/api/icons/all?page=${currentPage}&count=${itemsPerPage}`;
      
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Failed to fetch icons');
      let data = await response.json();

      console.log('API Response:', data);

      // Update pagination state and handle out-of-bounds pages
      if (data.pagination) {
        const newTotalPages = data.pagination.totalPages;
        setTotalPages(newTotalPages);

        // If current page is out of bounds, reset to page 1 and refetch
        if (currentPage > newTotalPages) {
          console.log('Page out of bounds, resetting to page 1');
          setCurrentPage(1);
          // Re-fetch with page 1
          const retryEndpoint = search
            ? `${API_URL}/api/icons/search?query=${encodeURIComponent(search)}&page=1&count=${itemsPerPage}`
            : `${API_URL}/api/icons/all?page=1&count=${itemsPerPage}`;

          const retryResponse = await fetch(retryEndpoint);
          if (!retryResponse.ok) throw new Error('Failed to fetch icons');
          data = await retryResponse.json();
        }
      }
      
      const transformedIcons = (data.icons || []).map(icon => ({
        ...icon,
        id: icon.name,
        svg_path: icon.svg_path,
        // Always use "0 0 24 24" viewBox regardless of what comes from the API
        viewBox: "0 0 24 24"
      }));
      
      setIcons(transformedIcons);
    } catch (err) {
      console.error('Error fetching icons:', err);
      setError(err.message);
      setIcons([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search with active search state management
  const handleSearch = async () => {
    const trimmedSearch = searchTerm.trim();
    setActiveSearch(trimmedSearch); // Update the active search term
    setCurrentPage(1); // Reset to first page on new search
    await fetchIcons(trimmedSearch);
  };

  return (
    <ModalWindow
      isOpen={isOpen}
      onClose={onClose}
      title="Icon Gallery"
      headerContent={
        <>
          <div className="icon-picker-search">
            <input
              type="text"
              placeholder="Search icons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              onBlur={handleSearch}
            />
          </div>
          <div className="header-nav-controls">
            <button
              onClick={() => {
                const newPage = Math.max(1, currentPage - 1);
                setCurrentPage(newPage);
              }}
              disabled={currentPage === 1 || loading}
              className="header-nav-button"
              title="Previous page"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="header-nav-count">{currentPage}/{totalPages}</span>
            <button
              onClick={() => {
                const newPage = Math.min(totalPages, currentPage + 1);
                setCurrentPage(newPage);
              }}
              disabled={currentPage === totalPages || loading}
              className="header-nav-button"
              title="Next page"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </>
      }
      position={position || { x: 0, y: 0 }}
      onDragEnd={onDragEnd}
      zIndex={zIndex}
      onFocus={onFocus}
    >
      <div className="icon-picker">

        {error && (
          <div className="icon-picker-error">
            Error loading icons: {error}
          </div>
        )}
        
        <div className="icon-picker-grid">
          {icons.map((icon, index) => (
            <button
              key={icon.id || index}
              className={`icon-picker-item ${
                currentIcon?.svg_path === icon.svg_path ? 'selected' : ''
              }`}
              onClick={() => {
                console.log('Raw icon data:', icon);
                // Pass the raw icon data directly
                onSelect(icon, false); // false means don't close after selection
              }}
              title="Click to select this icon"
            >
              <div className="icon-picker-item-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  style={{ verticalAlign: 'middle' }}
                  className="icon-picker-svg"
                >
                  <path 
                    d={icon.svg_path}
                  />
                </svg>
              </div>
            </button>
          ))}
        </div>
      </div>
    </ModalWindow>
  );
};

export default IconPicker;